import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Lottie from 'lottie-react';
import IntelService from '../../../../../../services/IntelService';
import Button from '../../../../../Button';
import classes from './styles.module.scss';
import loaderAnimation from '../../../../../../assets/animations/loader.json';
import fileIcons from '../../../../../../constants/fileIcons';

export default function ResumeUploader({
  onUploadComplete,
  onSkip,
  onNeedHelp,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [, setUploadProgress] = useState(0);
  const [, setPollingProgress] = useState('');
  const [currentStage, setCurrentStage] = useState('');
  const [, setResumeTaskId] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const lastUploadedFileRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  const acceptedFileTypes = [
    'application/pdf',
    'application/msword',
    'image/jpeg',
    'image/png',
    'image/tiff',
    'image/tif',
    'image/jpg',
    'image/jpeg',
    'image/png',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    if (!acceptedFileTypes.includes(file.type)) {
      setError(
        t(
          'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.fileTypeError'
        )
      );
      return false;
    }
    if (file.size > maxFileSize) {
      setError(
        t(
          'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.fileSizeError'
        )
      );
      return false;
    }
    setError('');
    return true;
  };

  const pollResumeTaskStatus = async (taskId) => {
    setIsPolling(true);
    setCurrentStage('starting');
    setPollingProgress(t('intelCandidate.polling.starting'));

    pollIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await IntelService.getResumeTaskStatus(taskId);

        // Update current stage
        setCurrentStage(statusResponse.stage || 'processing');
        setPollingProgress(
          t('intelCandidate.polling.checking', {
            stage: statusResponse.stage,
            status: statusResponse.status,
          })
        );

        // Check if failed at any stage
        if (statusResponse.status === 'Failed') {
          clearInterval(pollIntervalRef.current);
          setIsPolling(false);
          setCurrentStage('');
          setError(t('intelCandidate.errors.processingFailed'));
          return;
        }

        // Check if reached target stage and status
        if (
          statusResponse.stage === 'LlmConversion' &&
          statusResponse.status === 'Done'
        ) {
          clearInterval(pollIntervalRef.current);
          setIsPolling(false);
          setCurrentStage('completed');
          setPollingProgress(t('intelCandidate.polling.completed'));

          // Get the final result and automatically proceed to form
          try {
            const resultResponse = await IntelService.getResumeTaskResult(
              taskId
            );
            setCurrentStage('');
            setPollingProgress(t('intelCandidate.polling.resultReady'));

            // Automatically proceed to form instead of showing success message
            if (onUploadComplete && resultResponse) {
              onUploadComplete(resultResponse, lastUploadedFileRef.current);
            }
          } catch (resultError) {
            setError(t('intelCandidate.errors.resultFetchFailed'));
            setCurrentStage('');
            // eslint-disable-next-line no-console
            console.error('Failed to fetch result:', resultError);
          }
        }
      } catch (pollError) {
        clearInterval(pollIntervalRef.current);
        setIsPolling(false);
        setCurrentStage('');
        setError(t('intelCandidate.errors.pollingFailed'));
        // eslint-disable-next-line no-console
        console.error('Polling error:', pollError);
      }
    }, 2000); // Poll every 2 seconds

    // Set a timeout to stop polling after 5 minutes
    pollTimeoutRef.current = setTimeout(() => {
      clearInterval(pollIntervalRef.current);
      if (isPolling) {
        setIsPolling(false);
        setError(t('intelCandidate.errors.pollingTimeout'));
      }
    }, 300000); // 5 minutes timeout
  };

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const handleFileUpload = async (file) => {
    if (!validateFile(file)) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError('');
    setResumeTaskId(null);
    setUploadedFileName(file.name);
    lastUploadedFileRef.current = file;

    try {
      // Upload the file with real progress tracking
      const uploadResponse = await IntelService.uploadResumeFile(
        file,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      if (!uploadResponse || !uploadResponse.id) {
        throw new Error('Invalid upload response');
      }

      setResumeTaskId(uploadResponse.id);
      setIsUploading(false);

      // Start polling for status
      await pollResumeTaskStatus(uploadResponse.id);
    } catch (uploadError) {
      setIsUploading(false);
      setUploadProgress(0);
      setError(t('intelCandidate.errors.uploadFailed'));
      // eslint-disable-next-line no-console
      console.error('Upload error:', uploadError);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
    // Reset the file input value to allow re-uploading the same file
    e.target.value = '';
  };

  const handleCancelUpload = () => {
    // Stop polling if in progress
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }

    // Reset all states
    setIsUploading(false);
    setIsPolling(false);
    setUploadProgress(0);
    setPollingProgress('');
    setCurrentStage('');
    setError('');
    setResumeTaskId(null);
    setUploadedFileName('');
  };

  const getUploadingFileIcon = () => {
    const file = lastUploadedFileRef.current;
    if (!file) {
      return null;
    }

    const mimeType = file.type || '';
    const mimeSubtype = mimeType.split('/').pop();
    const extension = (file.name || '').split('.').pop()?.toLowerCase();

    // Prefer explicit mapping by MIME subtype (e.g., pdf, msword, vnd.openxml...)
    if (mimeSubtype && fileIcons[mimeSubtype]) {
      return fileIcons[mimeSubtype];
    }

    // Generic image fallback
    if (mimeType.startsWith('image/')) {
      return fileIcons.jpg || fileIcons.png;
    }

    // Fallback to extension mapping
    if (extension && fileIcons[extension]) {
      return fileIcons[extension];
    }

    return null;
  };

  const getLoadingMessage = () => {
    if (isUploading) {
      return t('intelCandidate.upload.uploading') || 'Uploading resume...';
    }

    // Dynamic messages based on current stage
    switch (currentStage) {
      case 'starting':
        return (
          t('intelCandidate.polling.stages.starting') ||
          'Initializing processing...'
        );
      case 'PdfToText':
        return (
          t('intelCandidate.polling.stages.pdfToText') ||
          'Extracting text from resume...'
        );
      case 'LlmConversion':
        return (
          t('intelCandidate.polling.stages.llmConversion') ||
          'Analyzing candidate details...'
        );
      case 'completed':
        return (
          t('intelCandidate.polling.stages.completed') ||
          'Processing completed!'
        );
      default:
        return t('intelCandidate.polling.processing') || 'Processing resume...';
    }
  };

  return (
    <div className={classes.uploader}>
      {/* Upload Area */}
      <div
        className={`${classes.uploadArea} ${
          isDragActive ? classes.dragActive : ''
        } ${error ? classes.errorState : ''} ${
          isUploading || isPolling ? classes.processing : ''
        }`}
        onDrop={isUploading || isPolling ? undefined : handleDrop}
        onDragOver={isUploading || isPolling ? undefined : handleDragOver}
        onDragLeave={isUploading || isPolling ? undefined : handleDragLeave}
      >
        <div className={classes.uploadContent}>
          {(() => {
            if (isUploading || isPolling) {
              return (
                // Show progress state with loading animation
                <div className={classes.progressContent}>
                  {/* File Preview Card with Loading Animation */}
                  <div className={classes.filePreviewCard}>
                    {/* Loading Animation */}
                    <div className={classes.loadingAnimationContainer}>
                      <Lottie animationData={loaderAnimation} loop />
                    </div>

                    {/* File Icon */}
                    <div className={classes.fileIcon}>
                      {getUploadingFileIcon() ? (
                        <img
                          src={getUploadingFileIcon()}
                          alt="File"
                          width={64}
                          height={64}
                        />
                      ) : (
                        <svg
                          width="64"
                          height="64"
                          viewBox="0 0 64 64"
                          fill="none"
                        >
                          <rect
                            x="16"
                            y="8"
                            width="32"
                            height="48"
                            rx="2"
                            fill="#FF6B47"
                          />
                          <rect
                            x="20"
                            y="20"
                            width="24"
                            height="2"
                            fill="white"
                          />
                          <rect
                            x="20"
                            y="26"
                            width="20"
                            height="2"
                            fill="white"
                          />
                          <rect
                            x="20"
                            y="32"
                            width="24"
                            height="2"
                            fill="white"
                          />
                        </svg>
                      )}
                    </div>

                    {/* File Name */}
                    <p className={classes.fileName}>
                      {uploadedFileName || 'File Name 1'}
                    </p>
                  </div>

                  {/* Dynamic Status Text */}
                  <p className={classes.statusText}>{getLoadingMessage()}</p>

                  {/* Cancel Button */}
                  <button
                    type="button"
                    className={classes.cancelButton}
                    onClick={handleCancelUpload}
                  >
                    {t('intelCandidate.actions.cancel') || 'Cancel'}
                  </button>
                </div>
              );
            }

            return (
              // Show initial upload state
              <div className={classes.uploadText}>
                <h2 className={classes.dragText}>
                  {t(
                    'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.dragDropResume'
                  )}
                </h2>
                <p className={classes.orText}>
                  {t(
                    'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.or'
                  )}
                </p>
                <Button
                  variant="blackWhite"
                  height={45}
                  onClick={handleBrowseClick}
                  disabled={isUploading || isPolling}
                >
                  {t(
                    'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.browse'
                  )}
                </Button>
              </div>
            );
          })()}

          {!isUploading && !isPolling && (
            <div className={classes.uploadInfo}>
              {error ? (
                <>
                  <div className={classes.errorSection}>
                    <p className={classes.errorText}>{error}</p>
                  </div>
                  <p className={classes.parseDescription}>
                    {t(
                      'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.parseDescription'
                    )}
                  </p>
                </>
              ) : (
                <>
                  <div className={classes.acceptedSection}>
                    <p className={classes.acceptedTitle}>
                      {t(
                        'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.accepted'
                      )}
                    </p>
                    <p className={classes.fileTypes}>
                      {t(
                        'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.fileTypes'
                      )}
                    </p>
                  </div>
                  <p className={classes.parseDescription}>
                    {t(
                      'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.parseDescription'
                    )}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={classes.footer}>
        {(() => {
          if (isUploading || isPolling) {
            return null; // Cancel button is now in the main content area
          }

          return (
            <>
              <button
                type="button"
                className={classes.skipButton}
                onClick={onSkip}
              >
                {t(
                  'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.skipUpload'
                )}
              </button>
              <button
                type="button"
                className={classes.helpButton}
                onClick={onNeedHelp}
              >
                {t(
                  'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.needHelp'
                )}
              </button>
            </>
          );
        })()}
      </div>

      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.tiff, .tif, .jpg, .jpeg, .png"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
}
